import React from "react";
import ReactDOM from "react-dom";
import { photoUrl } from "@/lib/api";

/**
 * Full-screen lightbox with keyboard navigation (← → Esc).
 * Renders via a portal so it sits above modals, 3D canvas overlays and toasts.
 *
 * Props:
 *   - photos: string[]           list of photo refs (paths or URLs)
 *   - index: number | null       currently displayed index; when null the lightbox is hidden
 *   - onClose(): void
 *   - onPrev(): void
 *   - onNext(): void
 */
export function PhotoLightbox({ photos, index, onClose, onPrev, onNext }) {
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  React.useEffect(() => {
    if (index == null) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [index]);

  if (index == null || !photos[index]) return null;
  const src = photos[index];

  const overlay = (
    <div
      className="fixed inset-0 bg-black/95 flex flex-col items-center justify-center p-4"
      style={{ zIndex: 2147483000 }}
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 bg-white text-zinc-900 w-10 h-10 flex items-center justify-center text-xl font-bold border-2 border-zinc-900 hover:bg-red-600 hover:text-white"
        data-testid="lightbox-close"
        aria-label="Fermer"
      >
        ×
      </button>
      <div
        className="absolute top-4 left-4 bg-white text-zinc-900 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border-2 border-zinc-900"
        data-testid="lightbox-counter"
      >
        {index + 1} / {photos.length}
      </div>
      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white text-zinc-900 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-2xl font-bold border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white"
            data-testid="lightbox-prev"
            aria-label="Précédente"
          >
            ‹
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white text-zinc-900 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-2xl font-bold border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white"
            data-testid="lightbox-next"
            aria-label="Suivante"
          >
            ›
          </button>
        </>
      )}
      <img
        src={photoUrl(src)}
        alt={`photo-${index}`}
        className="max-w-full max-h-[88vh] object-contain border border-zinc-700"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
      <div className="mt-3 text-xs uppercase tracking-widest text-zinc-400 font-bold pointer-events-none">
        Cliquer ailleurs ou ESC pour fermer
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? ReactDOM.createPortal(overlay, document.body)
    : overlay;
}

export default PhotoLightbox;
